/**
 * @fileOverview This file defines the `DeleteRegionModal` component, which provides a modal dialog
 * to confirm and handle the deletion of a region.
 * @version 1.0.0
 * @since 1.0.0
 * @author Santiago Cano Herrera
 */

//#region Imports
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleChevronLeft,
  faTimes,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Skeleton,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
import { useTranslationHook } from '../../../../hooks/UseTranslationHook';
//#region

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

/**
 * Component for displaying a modal to confirm the deletion of a region.
 * @param {Object} props - The component props.
 * @param {number} props.regionId - The ID of the region to delete.
 * @param {boolean} props.showModal - Indicates whether the modal is open.
 * @param {function} props.setShowModal - Function to toggle the modal's visibility.
 * @param {function} props.refreshData - Function to refresh the regions data after deletion.
 * @returns {JSX.Element} The `DeleteRegionModal` component.
 */
export const DeleteRegionModal = ({
  regionId,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking deletion process
  const [isFetching, setIsFetching] = useState(false); // State for tracking data fetching
  const [region, setRegion] = useState({ id: '', name: '', notes: '' }); // State for the selected region details

  const { modalYesString, modalNoString } = useTranslationHook(); // Translation strings for modal buttons

  useEffect(() => {
    if (showModal) {
      fetchData(); // Fetch region data when the modal is opened
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Fetches the details of the selected region.
   */
  const fetchData = async () => {
    setIsFetching(true);
    const endpoint = `${GlobalVariables.getURL()}region/${regionId}`;

    try {
      const response = await customAxios.get(endpoint, HEADERS);
      const region = response.data.data;
      setRegion({ id: region.id, name: region.name, notes: region.notes }); // Update region details
    } catch (error) {
      console.error('Error fetching region data:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  /**
   * Closes the modal and refreshes the regions data.
   */
  const handleClose = () => {
    setShowModal(false);
    refreshData();
  };

  /**
   * Handles the deletion of the selected region.
   */
  const handleDelete = async () => {
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}region/delete/${regionId}`;

    try {
      const response = await customAxios.delete(endpoint, HEADERS);

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Región eliminada exitosamente',
        });
        handleClose(); // Close the modal after successful deletion
      }
    } catch (error) {
      console.error('Error deleting region:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      fullWidth={true}
      maxWidth='md'
    >
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Eliminar región
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='h5'>
              ¿Seguro que deseas eliminar esta región?
            </Typography>
          </Grid>
          {/* Display region ID */}
          <Grid item xs={12} md={2}>
            {isFetching ? (
              <Skeleton variant='text' width={100} height={30} />
            ) : (
              <Typography variant='h6'>
                <span style={{ fontWeight: 'bold' }}>ID: </span>
                {region.id}
              </Typography>
            )}
          </Grid>
          {/* Display region name */}
          <Grid item xs={12} md={10}>
            {isFetching ? (
              <Skeleton variant='text' width={300} height={30} />
            ) : (
              <Typography variant='h6'>
                <span style={{ fontWeight: 'bold' }}>
                  Nombre de la región:{' '}
                </span>
                {region.name}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {/* Confirm Delete Button */}
        <button
          className='modal-button'
          onClick={handleDelete}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faWarning} /> {modalYesString}
        </button>
        {/* Cancel Button */}
        <button
          className='modal-button no'
          onClick={handleClose}
          disabled={isLoading}
        >
          <FontAwesomeIcon icon={faCircleChevronLeft} /> {modalNoString}
        </button>
      </DialogActions>
    </Dialog>
  );
};
